"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import ServiceQueueForm from "@/components/templates/ServiceQueueForm";
import {
  buildFormInitialData,
  buildServiceQueueFormSchema,
  normalizeServiceQueueFormData,
} from "@/components/templates/ServiceQueueForm/form-data";
import { ServiceQueueFormData } from "@/components/templates/ServiceQueueForm/types";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useCreateServiceQueue from "@/hooks/requests/service-queue/use-create-service-queue";
import useGetServiceQueueById from "@/hooks/requests/service-queue/use-get-service-queue-by-id";
import useUpdateServiceQueue from "@/hooks/requests/service-queue/use-update-service-queue";
import useMutate from "@/hooks/use-mutate";
import { ServiceQueueFormMessages } from "@/messages/service-queue/service-queue-form.messages";
import { BreadCrumb } from "@/types/page.types";

type Props = {
  messages: ServiceQueueFormMessages;
  isViewing?: boolean;
};

function ServiceQueueFormPage({ messages, isViewing = false }: Props) {
  const { serviceQueueId } = useParams();
  const isEditing = !!serviceQueueId && !isViewing;
  const isLoadingExisting = !!serviceQueueId;
  const _id = serviceQueueId ? Number(serviceQueueId) : 0;
  const { data, isLoading: isLoadingServiceQueue, error } = useGetServiceQueueById({ _id });
  const { create, isLoading: isLoadingCreate } = useCreateServiceQueue();
  const { update, isLoading: isLoadingUpdate } = useUpdateServiceQueue();
  const { mutate } = useMutate();
  const router = useNavigate();
  const { lang } = useLanguage();

  const onSuccess = () => {
    mutate(SWR_KEYS.getServiceQueueById);
    mutate(SWR_KEYS.getServiceQueues);
    router(ROUTE_URLS.serviceQueue(lang));
  };

  const handleUpdate = async (_formData: ServiceQueueFormData) => {
    if (!data) return;

    const queueId = Number(data.id);
    const formData = normalizeServiceQueueFormData(_formData);
    const successMessage = messages.updateServiceQueueSuccess;
    const errorMessage = messages.updateServiceQueueError;

    await update({ _id: queueId, formData, successMessage, errorMessage, onSuccess });
  };

  const handleCreate = async (_formData: ServiceQueueFormData) => {
    const formData = normalizeServiceQueueFormData(_formData);
    const successMessage = messages.createServiceQueueSuccess;
    const errorMessage = messages.createServiceQueueError;

    await create({ formData, successMessage, errorMessage, onSuccess });
  };

  const handleSubmitForm = async (_formData: ServiceQueueFormData) => {
    if (isViewing) return;
    if (isEditing) await handleUpdate(_formData);
    else await handleCreate(_formData);
  };

  const breadCrumb = { title: messages.breadCrumbTitle, href: ROUTE_URLS.serviceQueue(lang) };
  const BREAD_CRUMBS: BreadCrumb[] = [breadCrumb];

  const isSuspended = isLoadingExisting ? isLoadingServiceQueue : false;
  const isLoading = isLoadingCreate || isLoadingUpdate || isLoadingServiceQueue;
  const pageTitle = isViewing
    ? messages.viewPageTitle
    : isEditing
      ? messages.updatePageTitle
      : messages.createPageTitle;

  const shouldLoadForm = isLoadingExisting ? !!data && !error : true;
  const shouldLoadError = !data && !!error && !isLoading && isLoadingExisting;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isSuspended}>
          {shouldLoadForm && (
            <Formik
              initialValues={buildFormInitialData(data)}
              validationSchema={buildServiceQueueFormSchema(messages)}
              validateOnChange={false}
              enableReinitialize
              onSubmit={handleSubmitForm}
            >
              <ServiceQueueForm
                messages={messages}
                isLoading={isLoading}
                isViewing={isViewing}
              />
            </Formik>
          )}

          {!!shouldLoadError && (
            <Card sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <AlertBlock
                title={messages.serviceQueueErrorToLoad}
                description={error.message}
                alt={messages.serviceQueueNotFoundAlt}
              />
            </Card>
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default ServiceQueueFormPage;
